import React, { useEffect, useRef, ReactNode, CSSProperties } from 'react';
import { motion, AnimatePresence, Transition } from 'framer-motion';

// 动画配置接口
interface AnimationConfig {
  initial?: any;
  animate?: any;
  exit?: any;
  transition?: Transition;
}

// 对话框动画配置接口
interface DialogAnimationConfig {
  overlay: AnimationConfig;
  dialog: AnimationConfig;
}

// 新增：标题配置接口
interface TitleConfig {
  icon?: ReactNode; // SVG 图标
  text: string | ReactNode; // 文字内容
  iconPosition?: 'left' | 'right'; // 图标位置，默认左侧
  iconClassName?: string; // 图标自定义样式类
  textClassName?: string; // 文字自定义样式类
  spacing?: string; // 图标和文字之间的间距，默认 'gap-2'
}

// 对话框组件的属性接口
interface DialogProps {
  visible: boolean;
  title?: ReactNode | TitleConfig; // 支持原有方式和新的配置方式
  onClose: () => void;
  showClose?: boolean;
  width?: string | number;
  top?: string | number;
  closeOnClickModal?: boolean;
  closeOnPressEscape?: boolean;
  center?: boolean;
  children?: ReactNode;
  footer?: ReactNode;
  customClass?: string;
  headerStyle?: CSSProperties;
  bodyStyle?: CSSProperties;
  footerStyle?: CSSProperties;
  overlayStyle?: CSSProperties;
  closeIconStyle?: CSSProperties;
  animation?: DialogAnimationConfig;
  // 添加模糊效果的强度
  backdropBlur?: number;
  // 移动端的样式设置
  mobileFullScreen?: boolean;
  mobileWidth?: string;
  backgroundColor?: string;         // 对话框背景色
}

// 预定义动画配置
export const dialogAnimations: Record<string, DialogAnimationConfig> = {
  // 默认淡入淡出效果
  fade: {
    overlay: {
      initial: { opacity: 0 },
      animate: { opacity: 1 },
      exit: { opacity: 0 },
      transition: { duration: 0.2 }
    },
    dialog: {
      initial: { opacity: 0 },
      animate: { opacity: 1 },
      exit: { opacity: 0 },
      transition: { duration: 0.2 }
    }
  },
  // 从顶部滑入效果
  slideDown: {
    overlay: {
      initial: { opacity: 0 },
      animate: { opacity: 1 },
      exit: { opacity: 0 },
      transition: { duration: 0.2 }
    },
    dialog: {
      initial: { opacity: 0, y: -50 },
      animate: { opacity: 1, y: 0 },
      exit: { opacity: 0, y: -50 },
      transition: { type: "spring", stiffness: 400, damping: 30 }
    }
  },
  // 弹跳效果
  bounce: {
    overlay: {
      initial: { opacity: 0 },
      animate: { opacity: 1 },
      exit: { opacity: 0 },
      transition: { duration: 0.3 }
    },
    dialog: {
      initial: { opacity: 0, scale: 0.5 },
      animate: { opacity: 1, scale: 1 },
      exit: { opacity: 0, scale: 0.5 },
      transition: { type: "spring", stiffness: 300, damping: 20 }
    }
  },
  // 从右侧滑入效果
  slideRight: {
    overlay: {
      initial: { opacity: 0 },
      animate: { opacity: 1 },
      exit: { opacity: 0 },
      transition: { duration: 0.2 }
    },
    dialog: {
      initial: { opacity: 0, x: 100 },
      animate: { opacity: 1, x: 0 },
      exit: { opacity: 0, x: 100 },
      transition: { type: "spring", stiffness: 400, damping: 30 }
    }
  },
  // 移动端从底部滑入效果
  slideUp: {
    overlay: {
      initial: { opacity: 0 },
      animate: { opacity: 1 },
      exit: { opacity: 0 },
      transition: { duration: 0.2 }
    },
    dialog: {
      initial: { opacity: 0, y: 100 },
      animate: { opacity: 1, y: 0 },
      exit: { opacity: 0, y: 100 },
      transition: { type: "spring", stiffness: 400, damping: 30 }
    }
  }
};

// 默认动画配置
const defaultAnimation: DialogAnimationConfig = {
  overlay: {
    initial: { opacity: 0 },
    animate: { opacity: 1 },
    exit: { opacity: 0 },
    transition: { duration: 0.2 }
  },
  dialog: {
    initial: { opacity: 0, y: -20, scale: 0.95 },
    animate: { opacity: 1, y: 0, scale: 1 },
    exit: { opacity: 0, y: 20, scale: 0.95 },
    transition: { type: "spring", stiffness: 400, damping: 30 }
  }
};

// 自定义关闭图标组件
const CloseIcon: React.FC<{ style?: CSSProperties }> = ({ style }) => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={style}
  >
    <line x1="18" y1="6" x2="6" y2="18"></line>
    <line x1="6" y1="6" x2="18" y2="18"></line>
  </svg>
);

// 新增：判断是否为 TitleConfig 的类型守卫函数
const isTitleConfig = (title: any): title is TitleConfig => {
  return title && typeof title === 'object' && 'text' in title;
};

// 新增：渲染标题的组件
const TitleRenderer: React.FC<{ title: ReactNode | TitleConfig }> = ({ title }) => {
  if (!title) return null;
  
  // 如果是新的 TitleConfig 格式
  if (isTitleConfig(title)) {
    const { 
      icon, 
      text, 
      iconPosition = 'left', 
      iconClassName = '', 
      textClassName = '', 
      spacing = 'gap-2' 
    } = title;
    
    const iconElement = icon && (
      <span className={`inline-flex items-center ${iconClassName}`}>
        {icon}
      </span>
    );
    
    const textElement = (
      <span className={`${textClassName} font-['Inter'] text-lg font-normal leading-7 tracking-normal`}>
        {text}
      </span>
    );
    
    return (
      <div className={`flex items-center ${spacing}`}>
        {iconPosition === 'left' && iconElement}
        {textElement}
        {iconPosition === 'right' && iconElement}
      </div>
    );
  }
  
  // 原有的简单标题格式
  return (
    <h3 className="font-sans text-lg font-normal leading-7 tracking-normal">
      {title}
    </h3>
  );
};

// 防止滚动条抖动的工具函数
const getScrollbarWidth = () => {
  // 创建一个临时的div来测量滚动条宽度
  const outer = document.createElement('div');
  outer.style.visibility = 'hidden';
  outer.style.overflow = 'scroll';
  // 类型断言处理 IE 特有属性
  (outer.style as any).msOverflowStyle = 'scrollbar';
  
  document.body.appendChild(outer);

  const inner = document.createElement('div');
  outer.appendChild(inner);

  const scrollbarWidth = outer.offsetWidth - inner.offsetWidth;
  
  outer.parentNode?.removeChild(outer);
  
  return scrollbarWidth;
};

const Dialog: React.FC<DialogProps> = ({ 
  visible = false, 
  title, 
  onClose, 
  showClose = true,
  width = '30%',
  top = '15vh',
  closeOnClickModal = true,
  closeOnPressEscape = true,
  center = true, // 默认居中显示
  children,
  footer,
  customClass = '',
  headerStyle = {},
  bodyStyle = {},
  footerStyle = {},
  overlayStyle = {},
  closeIconStyle = {},
  animation = defaultAnimation,
  backdropBlur = 5, // 默认模糊值
  mobileFullScreen = false, // 移动端默认不全屏
  mobileWidth = '90%', // 移动端默认宽度
  backgroundColor = 'white',
}) => {
  const dialogRef = useRef<HTMLDivElement>(null);
  
  // 检测是否为移动设备 - 使用 useRef 避免频繁状态更新
  const isMobileRef = useRef(false);
  const [isMobile, setIsMobile] = React.useState(false);
  
  // 监听窗口大小变化 - 防抖处理
  useEffect(() => {
    let timeoutId: NodeJS.Timeout;
    
    const checkMobile = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        const mobile = window.innerWidth <= 768;
        isMobileRef.current = mobile;
        setIsMobile(mobile);
      }, 100); // 100ms 防抖
    };
    
    // 初始检查
    checkMobile();
    
    // 监听窗口大小变化
    window.addEventListener('resize', checkMobile);
    
    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', checkMobile);
    };
  }, []);
  
  // 处理ESC键关闭弹窗和防止背景滚动
  useEffect(() => {
    if (!visible) return;
    
    // 获取滚动条宽度
    const scrollbarWidth = getScrollbarWidth();
    
    // 保存原始样式
    const originalOverflow = document.body.style.overflow;
    const originalPaddingRight = document.body.style.paddingRight;
    
    // 检查是否有垂直滚动条
    const hasVerticalScrollbar = document.body.scrollHeight > window.innerHeight;
    
    const handleEscKey = (event: KeyboardEvent) => {
      if (closeOnPressEscape && event.key === 'Escape') {
        onClose();
      }
    };
    
    // 防止背景滚动，同时避免滚动条抖动
    document.body.style.overflow = 'hidden';
    
    // 如果原来有滚动条，添加padding来补偿宽度变化
    if (hasVerticalScrollbar && scrollbarWidth > 0) {
      document.body.style.paddingRight = `${scrollbarWidth}px`;
    }
    
    document.addEventListener('keydown', handleEscKey);
    
    return () => {
      // 恢复原始样式
      document.body.style.overflow = originalOverflow;
      document.body.style.paddingRight = originalPaddingRight;
      document.removeEventListener('keydown', handleEscKey);
    };
  }, [visible, closeOnPressEscape, onClose]);
  
  // 点击遮罩层关闭
  const handleOverlayClick = (e: React.MouseEvent) => {
    if (closeOnClickModal && e.target === e.currentTarget) {
      onClose();
    }
  };
  
  // 获取当前动画配置
  const getAnimation = () => {
    return animation;
  };
  
  // 获取弹窗样式 - 使用 useMemo 优化
  const getDialogStyles = React.useMemo(() => {
    if (isMobile) {
      return {
        width: mobileWidth,
        transform: center ? 'translate(-50%, -50%)' : 'none',
        top: center ? '50%' : 'auto',
        left: center ? '50%' : 'auto',
        margin: 0
      };
    } else {
      return {
        width: width,
        transform: center ? 'translate(-50%, -50%)' : 'none',
        top: center ? '50%' : 'auto',
        left: center ? '50%' : 'auto',
        margin: 0
      };
    }
  }, [isMobile, mobileWidth, width, center]);
  
  const currentAnimation = getAnimation();
  
  return (
    <AnimatePresence mode="wait">
      {visible && (
        <motion.div 
          key="overlay"
          className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-40"
          style={{
            ...overlayStyle,
            backdropFilter: `blur(${backdropBlur}px)`,
            WebkitBackdropFilter: `blur(${backdropBlur}px)` // Safari 支持
          }}
          onClick={handleOverlayClick}
          initial={currentAnimation.overlay.initial}
          animate={currentAnimation.overlay.animate}
          exit={currentAnimation.overlay.exit}
          transition={currentAnimation.overlay.transition}
        >
          <motion.div 
            key="dialog"
            ref={dialogRef}
            className={`dark:bg-dark-card-background bg-card-background rounded-2xl shadow-md relative overflow-hidden border border-[#252A47] ${customClass}`}
            style={{ 
              ...getDialogStyles,
              maxWidth: '100vw', // 防止在小屏幕上溢出
              maxHeight: '90vh', // 防止高度溢出
            }}
            onClick={(e) => e.stopPropagation()}
            initial={currentAnimation.dialog.initial}
            animate={currentAnimation.dialog.animate}
            exit={currentAnimation.dialog.exit}
            transition={currentAnimation.dialog.transition}
          >
            {/* 标题栏 */}
            {(title || showClose) && (
              <div 
                className="flex justify-between items-center px-6 py-4 text-primary dark:text-dark-primary border-light dark:border-dark-light" 
                style={headerStyle}
              >
                <TitleRenderer title={title} />
                {showClose && (
                  <button 
                    className="text-primary dark:text-dark-primary hover:opacity-70 focus:outline-none transition-opacity"
                    onClick={onClose}
                    style={closeIconStyle}
                    aria-label="关闭"
                  >
                    <CloseIcon />
                  </button>
                )}
              </div>
            )}
            
            {/* 内容区域 */}
            <div 
              className="px-6 pb-6"
              style={{
                ...bodyStyle,
                maxHeight: 'calc(90vh - 120px)'
              }}
            >
              {children}
            </div>
            
            {/* 底部按钮区域 */}
            {footer && (
              <div 
                className="px-4 py-3 sm:px-6 sm:py-4 bg-gray-50 border-t border-gray-200 flex justify-end gap-2"
                style={footerStyle}
              >
                {footer}
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default Dialog;

// 导出类型，方便使用
export type { TitleConfig, DialogProps };